import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';

export default function AboutPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const parallaxRef = useRef<HTMLImageElement>(null);

  // ----------------------------------------------------
  // Scroll listener – adds a shadow to the nav when the
  // page is scrolled more than 50 px.
  // ----------------------------------------------------
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      if (parallaxRef.current) {
        const offset = window.scrollY * 0.5;
        parallaxRef.current.style.transform = `translateY(${offset}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ----------------------------------------------------
  // IntersectionObserver – reveals sections when they
  // become visible in the viewport.
  // ----------------------------------------------------
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.target.id) {
            setVisibleSections(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.15 }
    );

    Object.values(sectionRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Helper to attach a ref to a section element
  const setSectionRef = (id: string) => (el: HTMLElement | null) => {
    sectionRefs.current[id] = el;
  };

  const isVisible = (id: string) => visibleSections.has(id);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  // ----------------------------------------------------
  // SEO meta‑tags & JSON‑LD schema
  // ----------------------------------------------------
  useEffect(() => {
    document.title =
      'About Us | Miami Comfort Group - Strategic Real Estate Consulting';

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta(
      'description',
      "Learn about Miami Comfort Group's strategic approach to real estate consulting. We combine decades of experience with cutting‑edge technology to guide global investors through every step of their investment journey."
    );

    setMeta(
      'keywords',
      'about Miami Comfort Group, real estate consulting team, Miami real estate experts, investment strategy, property management'
    );

    const canonicalHref = `${siteUrl}/about`;
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalHref);

    setMeta('last-modified', new Date().toISOString().split('T')[0]);

    const aboutPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About Miami Comfort Group',
      description:
        "Learn about Miami Comfort Group's strategic approach to real estate consulting, combining decades of experience with cutting‑edge technology for global investors.",
      url: canonicalHref,
      mainEntity: {
        '@type': 'RealEstateAgent',
        name: 'Miami Comfort Group',
        description:
          'Strategic real estate consulting partner for foreign and domestic investors',
        foundingLocation: {
          '@type': 'City',
          name: 'Miami',
        },
      },
    };

    let schemaScript = document.querySelector('script[data-schema="about"]');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.setAttribute('type', 'application/ld+json');
      schemaScript.setAttribute('data-schema', 'about');
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(aboutPageSchema);

    return () => {
      document.title = 'Miami Comfort Group';
    };
  }, [siteUrl]);

  // ----------------------------------------------------
  // Render
  // ----------------------------------------------------
  return (
    <>
      <div className="min-h-screen bg-[#FAFAF8] font-['Quicksand']">
        {/* Navigation */}
        <Navbar />

        {/* Hero Section */}
        <section className="relative min-h-[85vh] flex items-end overflow-hidden">
          <div className="absolute inset-0">
            <img
              ref={parallaxRef}
              src="https://public.readdy.ai/ai/img_res/9f19d439823695104ecf6bfa1c573343.jpg"
              alt="Miami skyline at golden hour"
              className="w-full h-full object-cover object-center will-change-transform"
              style={{ transition: 'transform 0.1s linear' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20"></div>
          </div>

          <div className="relative w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20 pb-16 md:pb-24">
            <div className="max-w-[800px]">
              <div className="inline-block px-4 py-2 rounded-full bg-[#B4E4E0]/20 backdrop-blur-sm mb-6 border border-[#B4E4E0]/30">
                <span className="text-white text-[12px] md:text-[13px] font-medium tracking-[2px] uppercase">
                  About Miami Comfort Group
                </span>
              </div>

              <h1 className="font-['Oxygen'] font-bold text-[40px] md:text-[56px] lg:text-[68px] leading-[1.08] text-white mb-6 tracking-tight">
                Your Strategic Partner in U.S. Real Estate Investment
              </h1>

              <p className="text-white/85 text-[17px] md:text-[20px] leading-[1.6] max-w-[620px]">
                Real estate consulting for global investors and professionals
                relocating to Miami.
              </p>

              {/* Compliance notice – Property Management */}
              <div className="mt-8 max-w-[620px] rounded-xl border border-white/15 bg-white/8 backdrop-blur-sm px-5 py-4">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-[2px]">
                    <i className="ri-information-line text-[16px] text-[#B4E4E0]"></i>
                  </div>
                  <div className="space-y-1">
                    <p className="text-white/90 text-[12px] font-semibold uppercase tracking-[1.5px]">
                      Property Management — Service Disclosure
                    </p>
                    <p className="text-white/70 text-[13px] leading-[1.65]">
                      Provided by an independent property management company within the MCG group. We offer a curated selection of vetted professionals so you can choose the best fit for your needs.
                    </p>
                    <p className="text-white/50 text-[12px] leading-[1.5] pt-1">
                      This service is not provided by Avanti Way Realty, LLC.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section
          id="mission-vision"
          ref={setSectionRef('mission-vision')}
          className="py-24 md:py-32 bg-[#FAFAF8]"
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20">
            <div
              className={`grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 transition-all duration-700 ${
                isVisible('mission-vision')
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              {/* Mission */}
              <div className="relative group">
                <div className="absolute inset-0 rounded-[28px] bg-gradient-to-br from-black to-[#1a1a1a] transform group-hover:scale-[1.01] transition-transform duration-500"></div>
                <div className="relative rounded-[28px] bg-black p-10 md:p-14 text-white overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-[#A7D0CC]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                  <div className="relative">
                    <div className="w-14 h-14 rounded-2xl bg-[#A7D0CC]/15 flex items-center justify-center mb-8">
                      <i className="ri-compass-3-line text-[28px] text-[#A7D0CC]"></i>
                    </div>
                    <h2 className="font-['Oxygen'] font-bold text-[32px] md:text-[38px] mb-6 leading-[1.15]">
                      Our Mission
                    </h2>
                    <p className="text-white/75 text-[16px] leading-[1.85]">
                      To provide comprehensive real estate consulting services that
                      empower foreign and domestic investors to make intelligent,
                      data-driven decisions in the U.S. market. We are committed
                      to delivering strategy before execution, clarity over noise,
                      and long-term vision over short-term trends.
                    </p>
                  </div>
                </div>
              </div>

              {/* Vision */}
              <div className="relative group">
                <div className="absolute inset-0 rounded-[28px] bg-gradient-to-br from-[#E8E2DB] to-[#DDD5CB] transform group-hover:scale-[1.01] transition-transform duration-500"></div>
                <div className="relative rounded-[28px] bg-[#E8E2DB] p-10 md:p-14 overflow-hidden">
                  <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#A7D0CC]/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
                  <div className="relative">
                    <div className="w-14 h-14 rounded-2xl bg-[#A7D0CC]/20 flex items-center justify-center mb-8">
                      <i className="ri-eye-line text-[28px] text-[#6B8A87]"></i>
                    </div>
                    <h2 className="font-['Oxygen'] font-bold text-[32px] md:text-[38px] text-black mb-6 leading-[1.15]">
                      Our Vision
                    </h2>
                    <p className="text-[#444] text-[16px] leading-[1.85]">
                      To be the most trusted strategic real estate consulting
                      firm for global investors seeking to enter or expand in the
                      U.S. market. We envision a future where every investment
                      decision is backed by comprehensive analysis, advanced
                      technology and AI-powered market analysis, and unwavering
                      support.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Meet the Founder */}
        <section
          id="founder"
          ref={setSectionRef('founder')}
          className="py-24 md:py-32 bg-[#F5F3EF]"
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20">
            <div
              className={`transition-all duration-700 delay-100 ${
                isVisible('founder')
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              <span className="text-[#A7D0CC] text-[13px] uppercase tracking-[3px] font-semibold">
                Meet the Founder
              </span>

              <div className="mt-10 bg-white rounded-[32px] shadow-[0_8px_40px_rgba(0,0,0,0.06)] overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-5">
                  {/* Left – Photo & Contact */}
                  <div className="lg:col-span-2 bg-[#E8E2DB] p-8 md:p-12 flex flex-col items-center text-center">
                    <div className="w-[220px] h-[220px] md:w-[260px] md:h-[260px] rounded-full overflow-hidden border-[5px] border-white shadow-[0_8px_32px_rgba(0,0,0,0.1)] mb-8">
                      <img
                        src="https://static.readdy.ai/image/3c69b41949a07696583ba6f972aa86db/6a42f0aa833c7f4b893086903a75d3dd.jpeg"
                        alt="Elisabet Sierra Bermejo - Licensed Real Estate Sales Associate at Miami Comfort Group"
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                    <h3 className="font-['Oxygen'] font-bold text-[26px] text-black mb-1">
                      Elisabet Sierra Bermejo
                    </h3>
                    <p className="text-[#555] text-[15px] mb-1">
                      Licensed Real Estate Sales Associate | Avanti Way Realty, LLC
                    </p>
                    <p className="text-[#A7D0CC] text-[13px] font-semibold mb-8">
                      License #SL3535387
                    </p>

                    <div className="space-y-3 w-full max-w-[260px]">
                      <a
                        href="tel:+17868568145"
                        className="flex items-center gap-3 text-[#444] hover:text-[#A7D0CC] transition-colors cursor-pointer group"
                      >
                        <div className="w-9 h-9 rounded-xl bg-white/80 flex items-center justify-center group-hover:bg-[#A7D0CC]/10 transition-colors">
                          <i className="ri-phone-line text-[16px] text-[#A7D0CC]"></i>
                        </div>
                        <span className="text-[14px]">+1 786 856 8145</span>
                      </a>
                      <a
                        href="mailto:hello@miamicomfort.group"
                        className="flex items-center gap-3 text-[#444] hover:text-[#A7D0CC] transition-colors cursor-pointer group"
                      >
                        <div className="w-9 h-9 rounded-xl bg-white/80 flex items-center justify-center group-hover:bg-[#A7D0CC]/10 transition-colors">
                          <i className="ri-mail-line text-[16px] text-[#A7D0CC]"></i>
                        </div>
                        <span className="text-[14px]">
                          hello@miamicomfort.group
                        </span>
                      </a>
                      <a
                        href="https://www.instagram.com/elisabet_comfyhouses/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-[#444] hover:text-[#A7D0CC] transition-colors cursor-pointer group"
                      >
                        <div className="w-9 h-9 rounded-xl bg-white/80 flex items-center justify-center group-hover:bg-[#A7D0CC]/10 transition-colors">
                          <i className="ri-instagram-line text-[16px] text-[#A7D0CC]"></i>
                        </div>
                        <span className="text-[14px]">
                          @elisabet_comfyhouses
                        </span>
                      </a>
                      <a
                        href="https://www.linkedin.com/in/elisabet-sierra/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-[#444] hover:text-[#A7D0CC] transition-colors cursor-pointer group"
                      >
                        <div className="w-9 h-9 rounded-xl bg-white/80 flex items-center justify-center group-hover:bg-[#A7D0CC]/10 transition-colors">
                          <i className="ri-linkedin-line text-[16px] text-[#A7D0CC]"></i>
                        </div>
                        <span className="text-[14px]">LinkedIn Profile</span>
                      </a>
                    </div>
                  </div>

                  {/* Right – Bio */}
                  <div className="lg:col-span-3 p-8 md:p-12 lg:p-14">
                    <h2 className="font-['Oxygen'] font-bold text-[34px] md:text-[42px] text-black leading-[1.15] mb-10">
                      From Madrid to Shanghai.<br />
                      In Miami since 2018.
                    </h2>

                    <div className="space-y-6 text-[#444] text-[16px] leading-[1.85]">
                      <p>
                        I know what it means to start over in a new city —
                        and that experience shapes every conversation I have
                        with clients relocating here.
                      </p>
                      <p>
                        Right now I&#39;m working with tech executives and
                        professionals moving to Miami as companies establish
                        new headquarters here. The window to find the right
                        home — before the market absorbs the demand — is narrow.
                        I help them move fast, with clarity and strategy.
                      </p>
                      <p>
                        My work sits at the intersection of relocation consulting,
                        investment strategy, and AI-powered market analysis. I
                        don&#39;t just show properties — I help clients understand
                        the market, structure their decisions, and move with
                        confidence.
                      </p>
                    </div>

                    <div className="mt-10 flex items-center gap-4 p-6 rounded-2xl bg-[#F7F5F2]">
                      <div className="w-1 h-12 bg-[#A7D0CC] rounded-full flex-shrink-0"></div>
                      <p className="font-['Oxygen'] font-bold text-[18px] text-black leading-[1.4]">
                        &ldquo;Investinging well starts with deciding well.&rdquo;
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section
          id="our-story"
          ref={setSectionRef('our-story')}
          className="py-24 md:py-32 bg-white"
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20">
            <div
              className={`grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20 transition-all duration-700 delay-100 ${
                isVisible('our-story')
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="lg:col-span-2">
                <div className="sticky top-32">
                  <span className="text-[#A7D0CC] text-[13px] uppercase tracking-[3px] font-semibold">
                    Our Story
                  </span>
                  <h2 className="font-['Oxygen'] font-bold text-[40px] md:text-[50px] leading-[1.12] text-black mt-4 mb-6">
                    Founded in Miami, the Gateway to the Americas
                  </h2>
                  <div className="w-16 h-[3px] bg-[#A7D0CC] rounded-full"></div>

                  <div className="mt-10 rounded-2xl overflow-hidden w-full h-[280px] md:h-[340px]">
                    <img
                      src="https://readdy.ai/api/search-image?query=Beautiful%20Miami%20downtown%20skyline%20reflected%20in%20Biscayne%20Bay%20water%20at%20twilight%20with%20warm%20amber%20and%20teal%20tones%2C%20modern%20luxury%20high%20rise%20buildings%20and%20palm%20trees%2C%20sophisticated%20urban%20real%20estate%20investment%20destination%2C%20architectural%20photography%20with%20dramatic%20sky%20and%20calm%20water%20reflections&width=600&height=450&seq=about-story-miami-2025&orientation=landscape"
                      alt="Miami skyline at twilight"
                      className="w-full h-full object-cover object-top rounded-2xl"
                    />
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3 space-y-7">
                <p className="text-[#333] text-[17px] leading-[1.85]">
                  Miami Comfort Group was born from a simple observation:
                  international investors needed more than just a real estate
                  agent—they needed a strategic partner who understood the
                  complexities of cross-border investment, legal structuring,
                  financing challenges, and long-term wealth building.
                </p>

                <p className="text-[#333] text-[17px] leading-[1.85]">
                  Based in Miami, Florida—the natural bridge between North and
                  South America—we recognized the unique position our city
                  holds for foreign investors from Canada, Latin America, and
                  Europe seeking to establish a presence in the United States
                  real estate market.
                </p>

                <p className="text-[#333] text-[17px] leading-[1.85]">
                  What started as a boutique consulting practice has evolved
                  into a full-service real estate advisory firm — combining
                  human expertise, advanced technology, and AI-powered market
                  intelligence. We serve investors from Canada, Latin America,
                  Europe, and high-net-worth professionals relocating from New
                  York, California, and beyond. Today, MCG is also the strategic
                  partner of choice for tech executives and corporate
                  professionals relocating to Miami as companies move their
                  headquarters to South Florida.
                </p>

                <div className="bg-[#F7F5F2] rounded-2xl p-8 md:p-10 border-l-[3px] border-[#A7D0CC] mt-10">
                  <div className="w-10 h-10 flex items-center justify-center mb-4">
                    <i className="ri-double-quotes-l text-[32px] text-[#A7D0CC]/40"></i>
                  </div>
                  <p className="font-['Oxygen'] font-bold text-[22px] md:text-[26px] text-black leading-[1.35] mb-3">
                    Investing well starts with deciding well.
                  </p>
                  <p className="text-[#888] text-[15px] italic">
                    This philosophy guides everything we do at Miami Comfort
                    Group.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Approach */}
        <section
          id="approach"
          ref={setSectionRef('approach')}
          className="py-24 md:py-32 bg-[#FAFAF8]"
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20">
            <div
              className={`text-center mb-16 md:mb-20 transition-all duration-700 ${
                isVisible('approach')
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              <span className="text-[#A7D0CC] text-[13px] uppercase tracking-[3px] font-semibold">
                How We Work
              </span>
              <h2 className="font-['Oxygen'] font-bold text-[40px] md:text-[52px] text-black mt-4 mb-5 leading-[1.1]">
                Our Approach
              </h2>
              <p className="text-[#888] text-[17px] max-w-[600px] mx-auto">
                A comprehensive methodology that combines experience,
                technology, and strategic thinking
              </p>
            </div>

            <div
              className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 transition-all duration-700 delay-200 ${
                isVisible('approach')
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              {[
                {
                  icon: 'ri-user-heart-line',
                  title: 'Human Expertise',
                  description:
                    'Deep real estate expertise across international markets, combined with firsthand relocation experience. We understand what it means to navigate a new city, a new legal system, and a new market.',
                },
                {
                  icon: 'ri-cpu-line',
                  title: 'Advanced Technology',
                  description:
                    'AI-powered market analysis, predictive modeling, and automated reporting systems that provide real-time insights and data-driven recommendations for every investment decision.',
                },
                {
                  icon: 'ri-route-line',
                  title: 'Strategic Planning',
                  description:
                    'Comprehensive roadmaps that align with your financial goals, risk tolerance, and long-term vision. We focus on strategy before execution, ensuring every step is purposeful.',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: 'Legal & Tax Optimization',
                  description:
                    'Coordination with top-tier legal and tax specialists to structure your investments for maximum protection and efficiency, including entity formation and compliance.',
                },
                {
                  icon: 'ri-global-line',
                  title: 'International Focus',
                  description:
                    'From Canada, Latin America, and Europe to domestic professionals relocating from New York or California — we understand the complexity of cross-border decisions.',
                },
                {
                  icon: 'ri-time-line',
                  title: 'Long-Term Partnership',
                  description:
                    'We are not transactional—we build lasting relationships. From initial consultation through acquisition, management, and portfolio growth, we are your single point of contact.',
                },
                {
                  icon: 'ri-building-2-line',
                  title: 'Corporate Relocation',
                  description:
                    'We work with tech executives and professionals relocating to Miami as companies establish new headquarters in South Florida. Strategy, speed, and local expertise.',
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="group relative bg-[#FAFAF8] rounded-2xl p-8 md:p-10 hover:bg-white hover:shadow-[0_8px_40px_rgba(0,0,0,0.06)] transition-all duration-400 border border-transparent hover:border-[#E8E2DB]"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#A7D0CC]/12 flex items-center justify-center mb-6 group-hover:bg-[#A7D0CC]/20 transition-colors">
                    <i className={`${item.icon} text-[24px] text-[#6B8A87]`}></i>
                  </div>
                  <h3 className="font-['Oxygen'] font-bold text-[20px] text-black mb-3">
                    {item.title}
                  </h3>
                  <p className="text-[#666] text-[15px] leading-[1.75]">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section
          id="values"
          ref={setSectionRef('values')}
          className="py-24 md:py-32 bg-black relative overflow-hidden"
        >
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#A7D0CC]/5 rounded-full blur-[120px]"></div>
            <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#D4AF8C]/5 rounded-full blur-[100px]"></div>
          </div>

          <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20">
            <div
              className={`text-center mb-16 md:mb-20 transition-all duration-700 ${
                isVisible('values')
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              <span className="text-[#A7D0CC] text-[13px] uppercase tracking-[3px] font-semibold">
                What Drives Us
              </span>
              <h2 className="font-['Oxygen'] font-bold text-[40px] md:text-[52px] text-white mt-4 mb-5 leading-[1.1]">
                Our Core Values
              </h2>
              <p className="text-white/50 text-[17px] max-w-[550px] mx-auto">
                The principles that guide every decision and interaction
              </p>
            </div>

            <div
              className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 transition-all duration-700 delay-200 ${
                isVisible('values')
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
            >
              {[
                {
                  icon: 'ri-lightbulb-line',
                  title: 'Clarity',
                  description:
                    'We communicate with transparency and precision, ensuring you understand every aspect of your investment journey.',
                },
                {
                  icon: 'ri-shield-star-line',
                  title: 'Security',
                  description:
                    'Your peace of mind is paramount. We prioritize asset protection, legal compliance, and risk mitigation in everything we do.',
                },
                {
                  icon: 'ri-rocket-line',
                  title: 'Innovation',
                  description:
                    'We embrace technology and data-driven insights to provide you with a competitive advantage in the market.',
                },
                {
                  icon: 'ri-heart-line',
                  title: 'Integrity',
                  description:
                    'We operate with the highest ethical standards, putting your interests first and building relationships based on trust.',
                },
              ].map((value, index) => (
                <div
                  key={index}
                  className="text-center p-8 rounded-2xl border border-white/8 bg-white/[0.03] hover:bg-white/[0.06] transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#A7D0CC]/10 flex items-center justify-center mx-auto mb-6">
                    <i className={`${value.icon} text-[26px] text-[#A7D0CC]`}></i>
                  </div>
                  <h3 className="font-['Oxygen'] font-bold text-[22px] text-white mb-3">
                    {value.title}
                  </h3>
                  <p className="text-white/55 text-[15px] leading-[1.75]">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="relative py-28 md:py-36 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Luxury%20modern%20Miami%20penthouse%20interior%20with%20panoramic%20floor%20to%20ceiling%20windows%20overlooking%20ocean%20at%20sunset%2C%20elegant%20minimalist%20living%20room%20with%20warm%20golden%20light%20streaming%20in%2C%20sophisticated%20real%20estate%20investment%20property%20with%20designer%20furniture%20and%20tropical%20plants%2C%20cinematic%20warm%20tones%20with%20teal%20and%20amber%20color%20palette&width=1600&height=700&seq=about-cta-mcg-2025&orientation=landscape"
              alt="Luxury Miami property"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/60 to-black/50"></div>
          </div>

          <div className="relative max-w-[800px] mx-auto px-6 md:px-10 lg:px-20 text-center">
            <h2 className="font-['Oxygen'] font-bold text-[36px] md:text-[48px] lg:text-[54px] text-white mb-6 leading-[1.12]">
              Ready to Start Your Investment Journey?
            </h2>
            <p className="text-white/80 text-[17px] md:text-[19px] leading-[1.65] mb-10 max-w-[600px] mx-auto">
              Whether you&#39;re an international investor, a professional
              relocating to Miami, or looking to grow your U.S. portfolio —
              let&#39;s start with a conversation.
            </p>
            <a
              href="/#contact"
              className="btn-pulse inline-flex items-center justify-center px-8 py-4 bg-[#A7D0CC] rounded-lg hover:bg-[#8CBEB9] transition-all duration-300 cursor-pointer shadow-lg hover:shadow-[0_8px_24px_rgba(167,208,204,0.35)] whitespace-nowrap"
            >
              <span className="text-black text-[16px] font-bold font-['Oxygen']">
                Schedule Consultation
              </span>
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#1A1A1A] relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#A7D0CC]/5 rounded-full blur-[120px]"></div>
            <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#D4AF8C]/5 rounded-full blur-[100px]"></div>
          </div>

          <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-20 py-16 md:py-20">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12 lg:gap-16">
              <div>
                <img
                  src="https://static.readdy.ai/image/3c69b41949a07696583ba6f972aa86db/7cc47221362597664001b9fe6feac620.png"
                  alt="Miami Comfort Group"
                  className="h-10 md:h-12 w-auto object-contain brightness-0 invert mb-8"
                />
                <h3 className="font-['Oxygen'] font-bold text-[24px] md:text-[28px] text-white mt-4 mb-5 leading-[1.1]">
                  Strategic Real Estate<br />Consulting
                </h3>
                <p className="text-white/50 text-[14px] leading-[1.6]">
                  Investing well starts with deciding well
                </p>
              </div>

              <div>
                <h4 className="font-['Oxygen'] font-bold text-[14px] text-white uppercase tracking-wider mb-5">
                  <Link to="/services" className="hover:text-[#A7D0CC] transition-colors">
                    Services
                  </Link>
                </h4>
                <div className="space-y-3">
                  {[
                    'Strategic Consulting',
                    'Company & Legal Structure',
                    'Financing & Loans',
                    'Market Studies',
                    'Property Search',
                    'Property Management',
                  ].map((link, idx) => (
                    <div key={idx}>
                      <Link
                        to="/services"
                        className="text-white/60 text-[14px] hover:text-[#A7D0CC] transition-colors block"
                      >
                        {link}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-['Oxygen'] font-bold text-[14px] text-white uppercase tracking-wider mb-5">
                  Company
                </h4>
                <div className="space-y-3">
                  <Link
                    to="/about"
                    className="text-white/60 text-[14px] hover:text-[#A7D0CC] transition-colors block"
                  >
                    About Us
                  </Link>
                  <Link
                    to="/relocation"
                    className="text-white/60 text-[14px] hover:text-[#A7D0CC] transition-colors block"
                  >
                    Relocation
                  </Link>
                  <a
                    href="/#why-mcg"
                    className="text-white/60 text-[14px] hover:text-[#A7D0CC] transition-colors cursor-pointer block"
                  >
                    Why MCG
                  </a>
                  <a
                    href="/#contact"
                    className="text-white/60 text-[14px] hover:text-[#A7D0CC] transition-colors cursor-pointer block"
                  >
                    Contact
                  </a>
                </div>
              </div>

              <div>
                <h4 className="font-['Oxygen'] font-bold text-[14px] text-white uppercase tracking-wider mb-5">
                  Contact
                </h4>
                <div className="space-y-3 text-white/60 text-[14px]">
                  <p>Elisabet Sierra Bermejo</p>
                  <p>+1 786 856 8145</p>
                  <p>Miami, FL, USA</p>
                </div>

                <div className="flex items-center gap-4 mt-8">
                  <a
                    href="https://www.instagram.com/elisabet_comfyhouses/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center hover:bg-[#A7D0CC]/15 transition-colors cursor-pointer"
                  >
                    <i className="ri-instagram-line text-[18px] text-white/50 hover:text-[#A7D0CC] transition-colors"></i>
                  </a>
                  <a
                    href="https://www.linkedin.com/in/elisabet-sierra/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center hover:bg-[#A7D0CC]/15 transition-colors cursor-pointer"
                  >
                    <i className="ri-linkedin-line text-[18px] text-white/50 hover:text-[#A7D0CC] transition-colors"></i>
                  </a>
                  <a
                    href="https://youtube.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center hover:bg-[#A7D0CC]/15 transition-colors cursor-pointer"
                  >
                    <i className="ri-youtube-line text-[18px] text-white/50 hover:text-[#A7D0CC] transition-colors"></i>
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-16 pt-6 border-t border-white/8 flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-white/35 text-[13px]">
                © 2026 Miami ComfortGroup. All rights reserved.
              </p>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/35 text-[13px] hover:text-[#A7D0CC] transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>

            {/* Legal Footer Text */}
            <div className="mt-8 pt-6 border-t border-white/8 text-center space-y-3">
              <p className="text-[11px] font-semibold text-[#cccccc]">
                Elisabet Sierra Bermejo · Licensed Real Estate Sales Associate<br />
                Avanti Way Realty, LLC · License #SL3535387
              </p>
              <p className="text-[10px] text-[#888888] max-w-[800px] mx-auto leading-relaxed">
                Miami Comfort Group LLC is a brand name used for marketing purposes only. 
                Real estate services are provided by Elisabet Sierra Bermejo, licensed 
                with Avanti Way Realty, LLC.
              </p>
              <p className="text-[10px] text-[#888888]">
                © 2026 Miami Comfort Group · miamicomfort.group · hello@miamicomfort.group
              </p>
              <p className="text-[9px] text-[#888888] max-w-[700px] mx-auto leading-relaxed pt-2">
                Equal Housing Opportunity. We are pledged to the letter and spirit of U.S. policy for equal housing opportunity throughout the nation.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
